import { useEffect, useState } from 'react';

/**
 * One booking engine, two skins (owner brief, 2026-10-21).
 *
 * `fleet` is today's booking flow, unchanged. `consumer` is the shorter set
 * of screens in `client/src/consumer/` over the same Redux booking state and
 * the same submit (hooks/useBookingSubmit.ts). Only the Aperio preset ever
 * resolves a skin: for Go Oil and every other tenant `initSkin` is never
 * called, `getSkin()` stays `fleet` and nothing on the page changes.
 *
 * Which skin, first rule that matches wins:
 *   1. `?skin=fleet` / `?skin=consumer` on the URL (the switch link, the
 *      marketing site's booking links, an AI assistant's prefilled link).
 *      A marketing-site link (`src=web-...`) that says `consumer` is
 *      ordinary consumer traffic, so it goes through the A/B below
 *      (SITE_CONSUMER_LINKS_IN_AB); a bare `?skin=consumer` (QA, agents,
 *      `src=api`) always gets the consumer skin.
 *   2. A signed-in fleet account (`fleet_manager` role) gets fleet.
 *   3. What this visit already got (session cookie `ap_skin`), so Back,
 *      a reload or a new tab never flips the skin.
 *   4. Default: the consumer skin. While AB_ENABLED is true, consumer
 *      traffic is instead split between the consumer skin and today's flow
 *      by AB_CONSUMER_SHARE, sticky per visitor (`ap_ab`).
 */

export type Skin = 'consumer' | 'fleet';
/** The A/B arm a visitor is in; `none` when the skin was not the test's. */
export type AbArm = 'consumer' | 'control' | 'none';
export type SkinReason = 'url' | 'account' | 'session' | 'ab' | 'default';

/**
 * The consumer skin's A/B against today's flow. Off (owner, 2026-10-21): every
 * consumer visitor gets the consumer skin, and an `ap_ab` cookie or an
 * `ab-control` session left from the A/B no longer holds anyone in the old
 * flow. Turn it back on to split again.
 */
export const AB_ENABLED = false;

/** Share of A/B consumer traffic that gets the consumer skin (0..1). */
export const AB_CONSUMER_SHARE = 0.5;

/**
 * The marketing site adds `skin=consumer` to every non-fleet booking link.
 * True: such a link (recognised by its `src=web-...`) is still split by the
 * A/B, otherwise the whole site's traffic would skip the test. Only a link
 * without a site `src` forces the consumer skin.
 */
export const SITE_CONSUMER_LINKS_IN_AB = true;

/**
 * True: the switch link is shown to visitors in the A/B's control arm too
 * (they can opt into the simple booking). False keeps the control arm
 * clean: it sees today's flow with no link.
 */
export const SHOW_SWITCH_IN_CONTROL_ARM = false;

export const SKIN_COOKIE = 'ap_skin';
export const AB_COOKIE = 'ap_ab';
export const SID_COOKIE = 'ap_sid';
/** The A/B assignment is kept this long (seconds). */
export const AB_MAX_AGE_S = 90 * 24 * 60 * 60;

export interface SkinInputs {
  /** `skin` from the URL, as given. */
  urlSkin?: string | null;
  /** `src` from the URL, as given. */
  urlSrc?: string | null;
  /** The `ap_skin` session cookie. */
  sessionValue?: string | null;
  /** The `ap_ab` cookie. */
  abValue?: string | null;
  /** The signed-in account holds the fleet_manager role. */
  fleetManager?: boolean;
  /** 0..1, for a new A/B assignment. */
  random?: () => number;
  /** Whether the A/B runs (default AB_ENABLED). */
  abEnabled?: boolean;
}

export interface SkinDecision {
  skin: Skin;
  arm: AbArm;
  reason: SkinReason;
  /** Value to write to `ap_skin` (session cookie). */
  sessionValue: string;
  /** Value to write to `ap_ab`, when a new assignment was made. */
  newAbValue: 'consumer' | 'control' | null;
}

const asSkin = (value?: string | null): Skin | null => {
  const v = String(value || '').trim().toLowerCase();
  return v === 'consumer' || v === 'fleet' ? v : null;
};

/** `ap_skin`: `consumer`, `fleet`, or `ab-consumer` / `ab-control`. */
export function parseSessionValue(
  value?: string | null,
): { skin: Skin; arm: AbArm } | null {
  const v = String(value || '').trim().toLowerCase();
  if (v === 'ab-consumer') return { skin: 'consumer', arm: 'consumer' };
  if (v === 'ab-control') return { skin: 'fleet', arm: 'control' };
  const skin = asSkin(v);
  return skin ? { skin, arm: 'none' } : null;
}

const sessionValueOf = (skin: Skin, arm: AbArm): string => {
  if (arm === 'consumer') return 'ab-consumer';
  if (arm === 'control') return 'ab-control';
  return skin;
};

const isSiteLink = (src?: string | null): boolean => (
  /^web-/i.test(String(src || '').trim())
);

/** The rules above, as a pure function (tested in tests/consumerSkin.spec). */
export function resolveSkin(inputs: SkinInputs): SkinDecision {
  const decide = (
    skin: Skin,
    arm: AbArm,
    reason: SkinReason,
    newAbValue: SkinDecision['newAbValue'] = null,
  ): SkinDecision => ({
    skin, arm, reason, sessionValue: sessionValueOf(skin, arm), newAbValue,
  });

  const abEnabled = inputs.abEnabled ?? AB_ENABLED;
  const fromUrl = asSkin(inputs.urlSkin);
  const throughAb = fromUrl === 'consumer'
    && abEnabled
    && SITE_CONSUMER_LINKS_IN_AB
    && isSiteLink(inputs.urlSrc);
  if (fromUrl && !throughAb) return decide(fromUrl, 'none', 'url');

  if (inputs.fleetManager) return decide('fleet', 'none', 'account');

  const session = parseSessionValue(inputs.sessionValue);
  // With the A/B off, a session pinned by it (ab-consumer / ab-control) is
  // re-decided, so nobody stays in the control arm's old flow.
  if (session && (abEnabled || session.arm === 'none')) {
    return decide(session.skin, session.arm, 'session');
  }

  if (!abEnabled) return decide('consumer', 'none', 'default');

  // Consumer traffic: the A/B.
  const stored = String(inputs.abValue || '').trim().toLowerCase();
  if (stored === 'consumer' || stored === 'control') {
    return decide(stored === 'consumer' ? 'consumer' : 'fleet', stored, 'ab');
  }
  const roll = (inputs.random || Math.random)();
  const arm = roll < AB_CONSUMER_SHARE ? 'consumer' : 'control';
  return decide(arm === 'consumer' ? 'consumer' : 'fleet', arm, 'ab', arm);
}

/* ------------------------------------------------------------ cookies -- */

export function readCookieValue(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  const found = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(prefix));
  if (!found) return null;
  try {
    return decodeURIComponent(found.slice(prefix.length));
  } catch {
    return found.slice(prefix.length);
  }
}

/** A first-party cookie on this host; a session cookie without maxAge. */
export function writeCookieValue(
  name: string,
  value: string,
  maxAgeSeconds?: number,
): void {
  if (typeof document === 'undefined') return;
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    'Path=/',
    'SameSite=Lax',
  ];
  if (maxAgeSeconds !== undefined) parts.push(`Max-Age=${maxAgeSeconds}`);
  if (window.location.protocol === 'https:') parts.push('Secure');
  try {
    document.cookie = parts.join('; ');
  } catch {
    // cookies blocked: the skin lasts the page
  }
}

/* -------------------------------------------------------------- state -- */

interface SkinState {
  enabled: boolean;
  skin: Skin;
  arm: AbArm;
  reason: SkinReason;
  sid: string;
}

let state: SkinState = {
  enabled: false,
  skin: 'fleet',
  arm: 'none',
  reason: 'default',
  sid: '',
};
const listeners = new Set<(s: SkinState) => void>();

const emit = () => listeners.forEach((fn) => fn(state));

/** For tests. */
export function resetSkin(): void {
  state = {
    enabled: false, skin: 'fleet', arm: 'none', reason: 'default', sid: '',
  };
  emit();
}

/** True only once the Aperio preset switched the skins on. */
export function skinsEnabled(): boolean {
  return state.enabled;
}

/** The skin in effect: always `fleet` unless the Aperio preset chose. */
export function getSkin(): Skin {
  return state.enabled ? state.skin : 'fleet';
}

export function getAbArm(): AbArm {
  return state.enabled ? state.arm : 'none';
}

/** The `ap_sid` session id the booking logs are keyed by. */
export function getSessionId(): string {
  return state.sid;
}

const newSid = (): string => {
  const bytes = new Uint8Array(12);
  try {
    window.crypto.getRandomValues(bytes);
  } catch {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

/** Free text an AI assistant's link carried (`?vehicle=` / `?vehicleId=`). */
export interface VehiclePrefill {
  text: string | null;
  vehicleId: number | null;
}

let vehiclePrefill: VehiclePrefill = { text: null, vehicleId: null };

/** The prefilled car of this landing, read once (then forgotten). */
export function takeVehiclePrefill(): VehiclePrefill {
  const out = vehiclePrefill;
  vehiclePrefill = { text: null, vehicleId: null };
  return out;
}

export function peekVehiclePrefill(): VehiclePrefill {
  return vehiclePrefill;
}

/**
 * Picks the skin for this landing (Aperio preset only; index.tsx), writes
 * `ap_skin` / `ap_ab`, and keeps the `vehicle` hand-off for the car step.
 * Reads the URL before the address hand-off strips it.
 */
export function initSkin(options: {
  search?: string;
  roles?: string[] | null;
  random?: () => number;
  abEnabled?: boolean;
} = {}): SkinState {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(options.search ?? window.location.search);
  } catch {
    params = new URLSearchParams();
  }
  const decision = resolveSkin({
    urlSkin: params.get('skin'),
    urlSrc: params.get('src'),
    sessionValue: readCookieValue(SKIN_COOKIE),
    abValue: readCookieValue(AB_COOKIE),
    fleetManager: !!options.roles?.includes('fleet_manager'),
    random: options.random,
    abEnabled: options.abEnabled,
  });
  writeCookieValue(SKIN_COOKIE, decision.sessionValue);
  if (decision.newAbValue) {
    writeCookieValue(AB_COOKIE, decision.newAbValue, AB_MAX_AGE_S);
  }
  let sid = readCookieValue(SID_COOKIE) || '';
  if (!/^[A-Za-z0-9_-]{8,64}$/.test(sid)) {
    sid = newSid();
    writeCookieValue(SID_COOKIE, sid);
  }
  const vehicleText = (params.get('vehicle') || '').trim().slice(0, 200);
  const vehicleId = Number.parseInt(params.get('vehicleId') || '', 10);
  vehiclePrefill = {
    text: vehicleText || null,
    vehicleId: Number.isFinite(vehicleId) && vehicleId > 0 ? vehicleId : null,
  };
  state = {
    enabled: true,
    skin: decision.skin,
    arm: decision.arm,
    reason: decision.reason,
    sid,
  };
  emit();
  return state;
}

/**
 * The visitor switched with the link at the top of the page. Explicit, so
 * it leaves the A/B arm (`none`) and holds for the rest of the visit.
 */
export function switchSkin(skin: Skin): void {
  if (!state.enabled || state.skin === skin) return;
  writeCookieValue(SKIN_COOKIE, skin);
  state = {
    ...state, skin, arm: 'none', reason: 'url',
  };
  emit();
}

export function subscribeSkin(fn: (s: SkinState) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** The skin in effect, re-rendering on a switch. `fleet` off Aperio. */
export function useSkin(): Skin {
  const [skin, setSkinState] = useState<Skin>(getSkin());
  useEffect(() => {
    setSkinState(getSkin());
    return subscribeSkin(() => setSkinState(getSkin()));
  }, []);
  return skin;
}

/** Whether the switch link may show (both skins; see the constant above). */
export function switchLinkAllowed(): boolean {
  if (!state.enabled) return false;
  if (state.arm === 'control' && !SHOW_SWITCH_IN_CONTROL_ARM) return false;
  return true;
}
